import {
    IFormContact,
    IFormSection1,
    IFormSection2,
    IFormSection3,
    IFormSection4,
    IFormSection5,
    IFormSection6,
    IFormSection7,
    IFormSection8,
    QuizForm,
} from '@interfaces/Quiz'
import {
    UseFormReturnType,
    hasLength,
    isInRange,
    isNotEmpty,
    joiResolver,
    useForm,
} from '@mantine/form'
import { def, est, fv, pmt, pv, ror } from '@utils/FinanceFormula'
import React, { createContext, useEffect, useState } from 'react'
import {
    FormContactSchema,
    FormFourSchema,
    FormThreeSchema,
    FormTwoSchema,
} from '@schema/QuizFormSchema'

interface IQuizContext extends QuizForm {
    currentStep?: number
    setCurrentStep?: React.Dispatch<React.SetStateAction<number>>
    nextStep?: () => void
    backStep?: () => void
    resetStep?: () => void
    form1?: UseFormReturnType<IFormSection1>
    form2?: UseFormReturnType<IFormSection2>
    form3?: UseFormReturnType<IFormSection3>
    form4?: UseFormReturnType<IFormSection4>
    form5?: UseFormReturnType<IFormSection5>
    form6?: UseFormReturnType<IFormSection6>
    form7?: UseFormReturnType<IFormSection7>
    form8?: UseFormReturnType<IFormSection8>
    formContact?: UseFormReturnType<IFormContact>
    data?: QuizForm
    quiz_id?: number
    setQuizId?: React.Dispatch<React.SetStateAction<number | undefined>>
    setQuiz?: (quizId: number) => void
}
export const QuizContext = createContext<IQuizContext>({})

const QuizProvider = ({ children }: { children: React.ReactNode }) => {
    const [currentStep, setCurrentStep] = useState(1)
    const [quiz_id, setQuizId] = useState<number | undefined>(undefined)

    const nextStep = () => {
        setCurrentStep((prev) => prev + 1)
    }
    const backStep = () => {
        setCurrentStep((prev) => prev - 1)
    }

    const setQuiz = (quizId: number) => {
        setQuizId(quizId)
    }

    const form1 = useForm<IFormSection1>({
        initialValues: {},
        validate: {
            person_type: isNotEmpty('กรุณากรอกข้อมูล'),
            person_id: (value, values) => {
                if (values.person_type === 2) {
                    if (value?.length !== 5) return `กรุณากรอกข้อมูล 5 หลัก`
                } else {
                    if (value?.length !== 5 && value?.length !== 6)
                        return `กรุณากรอกข้อมูล 5 หรือ 6 หลัก`
                }

                return null
            },
            question_1: isNotEmpty('กรุณากรอกข้อมูล'),
            question_2: isNotEmpty('กรุณากรอกข้อมูล'),
            question_3: isNotEmpty('กรุณากรอกข้อมูล'),
        },
    })

    const form2 = useForm<IFormSection2>({
        validate: joiResolver(FormTwoSchema),
    })
    const form3 = useForm<IFormSection3>({
        initialValues: {
            long_term_inflation_assumption: '3.50',
            estimated_rate_return_after_retirement: '2.50',
        },
        validate: joiResolver(FormThreeSchema),
    })
    const form4 = useForm<IFormSection4>({
        initialValues: {
            estimated_saving_source_for_retirement: 0,
            life_insurance_mtl: false,
            life_insurance_other: false,
        },
        validate: joiResolver(FormFourSchema),
    })
    const form5 = useForm<IFormSection5>({})
    const form6 = useForm<IFormSection6>({})
    const form7 = useForm<IFormSection7>({})
    const form8 = useForm<IFormSection8>({
        initialValues: {
            click_tracking_1: true,
        },
    })
    const formContact = useForm<IFormContact>({
        initialValues: {
            disclosing: 'true',
        },
        validate: joiResolver(FormContactSchema),
    })
    const [data, setData] = useState<QuizForm>({})

    useEffect(() => {
        if (
            form1.isDirty() ||
            form2.isDirty() ||
            form3.isDirty() ||
            form4.isDirty() ||
            form5.isDirty() ||
            form6.isDirty()
        )
            setData((prev) => {
                return {
                    ...form1.values,
                    ...form2.values,
                    ...form3.values,
                    ...form4.values,
                    ...form5.values,
                    ...form6.values,
                }
            })
        // eslint-disable-next-line
    }, [
        form1.values,
        form2.values,
        form3.values,
        form4.values,
        form5.values,
        form6.values,
        form7.values,
    ])

    useEffect(() => {
        form2.setFieldValue(
            'payment_after_retirement_each_year',
            data.payment_after_retirement_each_month! * 12
        )
        form2.setFieldValue(
            'years_before_retirement',
            data.retirement_age! - data.current_age! < 0
                ? 0
                : data.retirement_age! - data.current_age!
        )
        form3.setFieldValue(
            'payment_at_retirement_first_year',
            fv(
                Number(data.long_term_inflation_assumption!) / 100,
                data.years_before_retirement!,
                data.payment_after_retirement_each_year!
            )
        )

        form3.setFieldValue(
            'amount_retirement_years',
            Number(data.estimated_final_age!) - data.retirement_age! < 0
                ? 0
                : Number(data.estimated_final_age!) - data.retirement_age!
        )

        form3.setFieldValue(
            'rate_return_after_retirement_with_inflation',
            ror(
                Number(data.long_term_inflation_assumption!) / 100,
                Number(data.estimated_rate_return_after_retirement!) / 100
            ) * 100
        )

        let pv_data = pv(
            data.rate_return_after_retirement_with_inflation! / 100,
            data.amount_retirement_years!,
            data.payment_at_retirement_first_year!
        )
        pv_data = isNaN(pv_data) ? 0 : pv_data

        form3.setFieldValue('required_funds_for_retirement', pv_data)

        const est_data = est(
            data.expected_rate_of_return_before_retirement! / 100,
            data.years_before_retirement!,
            Number(data.provident_fund! ?? 0) +
                Number(data.assets_invested_to_reach_retirement_goal! ?? 0) +
                Number(data.social_security_fund! ?? 0) +
                Number(data.other_assets! ?? 0)
        )
        form4.setFieldValue(
            'estimated_saving_source_for_retirement',
            isNaN(est_data) ? 0 : est_data
        )

        form5.setFieldValue(
            'dificit_funds_retirement',
            def(
                data.required_funds_for_retirement!,
                data.estimated_saving_source_for_retirement!
            )
        )

        form6.setFieldValue(
            'investment_rate_for_expect_return_two_percent',
            pmt(
                0.02,
                data.years_before_retirement!,
                data.dificit_funds_retirement!
            )
        )

        form6.setFieldValue(
            'investment_rate_for_expect_return_four_percent',
            pmt(
                0.04,
                data.years_before_retirement!,
                data.dificit_funds_retirement!
            )
        )
        form6.setFieldValue(
            'investment_rate_for_expect_return_six_percent',
            pmt(
                0.06,
                data.years_before_retirement!,
                data.dificit_funds_retirement!
            )
        )

        form6.setFieldValue(
            'investment_rate_for_expect_return_eight_percent',
            pmt(
                0.08,
                data.years_before_retirement!,
                data.dificit_funds_retirement!
            )
        )

        form7.setFieldValue('person_id', data.person_id!)
        form7.setFieldValue('person_type', data.person_type!)
        form1.resetDirty()
        form2.resetDirty()
        form3.resetDirty()
        form4.resetDirty()
        form5.resetDirty()
        form6.resetDirty()
        form8.resetDirty()
        formContact.resetDirty()

        // eslint-disable-next-line
    }, [data])

    const resetStep = () => {
        form1.reset()
        form2.reset()
        form3.reset()
        form4.reset()
        form5.reset()
        form6.reset()
        form7.reset()
        form8.reset()
        formContact.reset()
        form1?.setFieldValue('person_type', data.person_type)
        setQuizId(undefined)
        setData({})
        setCurrentStep(1)
    }

    return (
        <QuizContext.Provider
            value={{
                form1,
                form2,
                form3,
                form4,
                form5,
                form6,
                form7,
                form8,
                formContact,
                data,
                currentStep,
                setCurrentStep,
                nextStep,
                backStep,
                resetStep,
                quiz_id,
                setQuizId,
                setQuiz,
                ...data,
            }}
        >
            {children}
        </QuizContext.Provider>
    )
}

export default QuizProvider
